import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export const ADSENSE_SLOTS = {
  HOME: '1641847085',       // flashweb1 (Display)
  TOOLS: '3349505410',      // flashweb2 (Display)
  BLOG: '4076438738',       // flashweb3 (Display)
  IN_ARTICLE: '6412537043', // flashweb4 (In-Article)
} as const;

export interface AdBannerProps {
  className?: string;
  slot?: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'horizontal';
  layout?: string;
  label?: string;
}

export function AdBanner({
  className = '',
  slot,
  format = 'auto',
  layout,
  label,
}: AdBannerProps) {
  const { t } = useTranslation();
  const adRef = useRef<HTMLModElement | null>(null);
  const pushedRef = useRef(false);
  const adClientId = (import.meta as any).env?.VITE_ADSENSE_CLIENT_ID || 'ca-pub-1322491498165761';

  const isInArticle = layout === 'in-article';
  const effectiveSlot = slot || (isInArticle || format === 'fluid' ? ADSENSE_SLOTS.IN_ARTICLE : ADSENSE_SLOTS.HOME);
  const minSlotHeight = format === 'rectangle' ? 250 : format === 'fluid' ? 120 : 90;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // S'assurer que le script officiel AdSense est présent dans le document
    if (!document.querySelector('script[src*="pagead2.googlesyndication.com"]')) {
      const script = document.createElement('script');
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adClientId}`;
      script.async = true;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }

    // Déclencher push({}) UNE SEULE FOIS par montage de composant
    if (!pushedRef.current) {
      try {
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
        pushedRef.current = true;
      } catch (e) {
        // Ignorer en silence si bloqué par une extension
      }
    }
  }, [adClientId, effectiveSlot]);

  return (
    <aside
      aria-label="Publicité sponsorisée"
      className={`w-full my-8 p-3.5 rounded-2xl bg-slate-100/70 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/10 relative overflow-hidden flex flex-col items-center justify-center transition-all ${className}`}
      style={{ minHeight: `${minSlotHeight + 35}px` }}
    >
      <div className="w-full flex items-center justify-start mb-2 text-[10px] font-mono uppercase tracking-widest text-slate-400/80 dark:text-slate-500/80">
        <span>{label || t('common.sponsored', 'Sponsorisé')}</span>
      </div>

      <div className="w-full flex justify-center overflow-hidden" style={{ minHeight: `${minSlotHeight}px` }}>
        <ins
          ref={adRef}
          className="adsbygoogle"
          style={{ display: 'block', width: '100%', minHeight: `${minSlotHeight}px`, textAlign: 'center' }}
          data-ad-client={adClientId}
          data-ad-slot={effectiveSlot}
          data-ad-format={format}
          {...(isInArticle ? { 'data-ad-layout': 'in-article' } : layout ? { 'data-ad-layout-key': layout } : {})}
          data-full-width-responsive="true"
        />
      </div>
    </aside>
  );
}
